import { Dispatch } from 'redux'

import {
  getAll,
  getDeleted,
  restore,
  copy,
  update,
  destroy,
  updateOrder,
  revokeFromDashboard,
  inviteToDashboard,
  putFilters
} from 'redux/api/DashboardGroups'
import * as UserEvents from 'redux/api/internal/UserEvents'

import { TDashboard } from 'types/GlobalDashboard'
import { DashboardStatus } from 'types/GlobalDashboardGroup'

import { Action, Context } from 'types/GlobalUserEvents'

import { useThunkActions } from 'redux/actions/useThunkActions'
import { isNewUserInvite } from 'utils/functions'
import { toggleSnackbarOpen } from '../Snackbar'
import { SnackbarLinkText, SnackbarMessage } from 'types/GlobalSnackbar'
import {
  TPatchDashboardGroup,
  TPostInviteToDashboardGroupPermission,
  TPostRevokeAccess,
  TPutDashboardGroupFilters
} from 'redux/api/DashboardGroups/types'

import * as actions from './actions'
import * as groupActions from '../Groups/actions'

export function tryGetAllDashboardGroups(dispatch: Dispatch) {
  dispatch(actions.getAll())

  getAll()
    .then((response) => dispatch(actions.getAllSuccess(response.data.data)))
    .catch(() => dispatch(actions.getAllFailed()))
}

function tryGetDeletedDashboardGroups() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getDeleted())

    getDeleted()
      .then((response) =>
        dispatch(actions.getDeletedSuccess(response.data.data))
      )
      .catch(() => dispatch(actions.getDeletedFailed()))
  }
}

function tryRestoreDashboardGroup(
  id: string,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.restore())

    restore(id)
      .then((response) => {
        dispatch(actions.restoreSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardRestored))

        if (callback) {
          callback(false)
        }
      })
      .catch(() => {
        dispatch(actions.restoreFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardRestoredFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

function tryUpdateDashboardOrder(id: string, newOrder: TDashboard[]) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateDashboardOrder())

    updateOrder(id, { data: newOrder.map((item) => item.id) })
      .then(() =>
        dispatch(actions.updateDashboardOrderSuccess({ id, order: newOrder }))
      )
      .catch(() => dispatch(actions.updateDashboardOrderFailed()))
  }
}

function tryCopyDashboardGroup(
  id: string,
  navigate: (id: string) => void,
  navigateOnSuccess = false
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.copy())

    copy(id)
      .then((response) => {
        const group = response.data.data

        // Create event for each dashboard in group.
        group.dashboards.forEach((dashboard) => {
          UserEvents.log({
            action: Action.DASHBOARD_CREATED,
            context: Context.DUPLICATE,
            dashboard_id: dashboard.id,
            dashboard_group_id: dashboard.dashboard_group_id
          })
        })

        dispatch(actions.copySuccess(response.data.data))

        if (navigateOnSuccess) {
          dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardDuplicated))

          navigate(group.dashboards[0].id)
        } else {
          dispatch(
            toggleSnackbarOpen(SnackbarMessage.DashboardDuplicated, {
              link: {
                link: () => navigate(group.dashboards[0].id),
                text: SnackbarLinkText.NavigateToDashboard
              }
            })
          )
        }
      })
      .catch(() => {
        dispatch(actions.copyFailed())
      })
  }
}

function tryUpdateDashboardGroup(
  id: string,
  data: TPatchDashboardGroup,
  callback?: (e: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, data)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        if (data.status !== undefined) {
          dispatch(actions.publishStatusChanged({ status: data.status }))
        }

        if (data.status === DashboardStatus.DRAFT) {
          // if status set to draft then remove from any group
          dispatch(
            groupActions.removeFromDashboard({
              dashboardGroupId: response.data.data.id
            })
          )
        }

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdated))

        if (callback) {
          callback(false)
        }
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdatedFailed))

        if (callback) {
          callback(true)
        }
      })
  }
}

function tryDeleteDashboardGroup(
  id: string,
  status: DashboardStatus,
  callback: () => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        callback()

        dispatch(actions.destroySuccess({ id, status }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardDeleted))
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardDeletedFailed))
      })
  }
}

function tryRevokeFromDashboard(id: string, body: TPostRevokeAccess) {
  return (dispatch: Dispatch) => {
    dispatch(actions.revokeAccess())

    revokeFromDashboard(id, body)
      .then((response) => {
        dispatch(actions.revokeAccessSuccess(response.data.data))

        if (body.group_id) {
          dispatch(
            groupActions.removeFromDashboard({
              groupId: body.group_id,
              dashboardGroupId: response.data.data.id
            })
          )
        }

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdated))
      })
      .catch(() => {
        dispatch(actions.revokeAccessFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdatedFailed))
      })
  }
}

function tryInviteToDashboard(
  id: string,
  data: TPostInviteToDashboardGroupPermission
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.invite())

    inviteToDashboard(id, data)
      .then((response) => {
        const isNewUser = isNewUserInvite(data)

        if (isNewUser) {
          UserEvents.log({
            action: Action.INVITE_SENT_TO_HOMEPAL,
            context: null,
            dashboard_id: null,
            dashboard_group_id: id
          })
        }

        UserEvents.log({
          action: Action.INVITE_SENT_TO_DASHBOARD,
          context: null,
          dashboard_id: null,
          dashboard_group_id: id
        })

        dispatch(actions.inviteSuccess(response.data.data))

        // Look into this.
        if (!isNewUser && data.group_id) {
          dispatch(
            groupActions.appendToDashboard({
              groupId: data.group_id,
              dashboardGroupId: response.data.data.id
            })
          )
        }

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdated))
      })
      .catch(() => {
        dispatch(actions.inviteFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdatedFailed))
      })
  }
}

function tryPutDashboardGroupFilterOptions(
  id: string,
  data: TPutDashboardGroupFilters
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.putDashboardFilters())

    putFilters(id, data)
      .then((response) =>
        dispatch(
          actions.putDashboardFiltersSuccess({ id, data: response.data.data })
        )
      )
      .catch(() => actions.putDashboardFiltersFailed())
  }
}

export const useDashboardGroupActions = () => {
  return {
    tryUpdateDashboardOrder: useThunkActions(tryUpdateDashboardOrder),
    tryCopyDashboardGroup: useThunkActions(tryCopyDashboardGroup),
    tryUpdateDashboardGroup: useThunkActions(tryUpdateDashboardGroup),
    tryDeleteDashboardGroup: useThunkActions(tryDeleteDashboardGroup),
    tryGetDeletedDashboardGroups: useThunkActions(tryGetDeletedDashboardGroups),
    tryRestoreDashboardGroup: useThunkActions(tryRestoreDashboardGroup),
    tryRevokeFromDashboard: useThunkActions(tryRevokeFromDashboard),
    tryInviteToDashboard: useThunkActions(tryInviteToDashboard),
    tryPutDashboardGroupFilterOptions: useThunkActions(
      tryPutDashboardGroupFilterOptions
    )
  }
}
